// @ts-check
/** @typedef {import('../types/api').Task} Task */
/** @typedef {import('../types/api').TaskComment} TaskComment */
/** @typedef {import('../types/api').TaskAttachment} TaskAttachment */
/** @typedef {import('../types/api').TaskAnalytics} TaskAnalytics */
/** @typedef {import('../types/api').TeamWorkload} TeamWorkload */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const taskService = {
  // ================================
  // TASK CRUD
  // ================================

  /**
   * @param {Object} taskData
   * @returns {Promise<Task>}
   */
  async createTask(taskData) {
    return api.post('/api/admin/tasks', taskData);
  },

  /**
   * @param {Object} [filters]
   * @returns {Promise<Task[]>}
   */
  async getTasks(filters = {}) {
    const params = new URLSearchParams();

    if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
    if (filters.created_by) params.append('created_by', filters.created_by);
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.due_date_from) params.append('due_date_from', filters.due_date_from);
    if (filters.due_date_to) params.append('due_date_to', filters.due_date_to);
    if (filters.tags) params.append('tags', filters.tags);
    if (filters.search) params.append('search', filters.search);
    if (filters.include_deleted) params.append('include_deleted', filters.include_deleted);
    if (filters.parent_task_id) params.append('parent_task_id', filters.parent_task_id);

    const data = await api.get(`/api/admin/tasks?${params.toString()}`);
    assertArray(data, 'taskService.getTasks');
    return data;
  },

  /**
   * @param {string} taskId
   * @returns {Promise<Task>}
   */
  async getTask(taskId) {
    const data = await api.get(`/api/admin/tasks/${taskId}`);
    assertShape(data, ['id'], 'taskService.getTask');
    return data;
  },

  /**
   * @param {string} taskId
   * @param {Object} taskData
   * @returns {Promise<Task>}
   */
  async updateTask(taskId, taskData) {
    return api.put(`/api/admin/tasks/${taskId}`, taskData);
  },

  /**
   * @param {string} taskId
   * @param {boolean} [hardDelete=false]
   * @returns {Promise<void>}
   */
  async deleteTask(taskId, hardDelete = false) {
    return api.del(`/api/admin/tasks/${taskId}?hard_delete=${hardDelete}`);
  },

  /**
   * @param {string} taskId
   * @returns {Promise<Task>}
   */
  async restoreTask(taskId) {
    return api.post(`/api/admin/tasks/${taskId}/restore`);
  },

  // ================================
  // TASK STATUS OPERATIONS
  // ================================

  /**
   * @param {string} taskId
   * @param {string} status
   * @returns {Promise<Task>}
   */
  async updateTaskStatus(taskId, status) {
    return api.put(`/api/admin/tasks/${taskId}/status?status=${status}`);
  },

  /**
   * @param {string} taskId
   * @param {string} [assignedTo]
   * @returns {Promise<Task>}
   */
  async assignTask(taskId, assignedTo) {
    const params = assignedTo ? `?assigned_to=${assignedTo}` : '';
    return api.put(`/api/admin/tasks/${taskId}/assign${params}`);
  },

  // ================================
  // TASK COMMENTS
  // ================================

  /**
   * @param {string} taskId
   * @param {string} commentText
   * @returns {Promise<TaskComment>}
   */
  async createComment(taskId, commentText) {
    return api.post(`/api/admin/tasks/${taskId}/comments`, { comment_text: commentText });
  },

  /**
   * @param {string} taskId
   * @returns {Promise<TaskComment[]>}
   */
  async getComments(taskId) {
    const data = await api.get(`/api/admin/tasks/${taskId}/comments`);
    assertArray(data, 'taskService.getComments');
    return data;
  },

  /**
   * @param {string} commentId
   * @param {string} commentText
   * @returns {Promise<TaskComment>}
   */
  async updateComment(commentId, commentText) {
    return api.put(`/api/admin/tasks/comments/${commentId}`, { comment_text: commentText });
  },

  /**
   * @param {string} commentId
   * @returns {Promise<void>}
   */
  async deleteComment(commentId) {
    return api.del(`/api/admin/tasks/comments/${commentId}`);
  },

  // ================================
  // TASK ATTACHMENTS
  // ================================

  /**
   * @param {string} taskId
   * @param {Object} attachmentData
   * @returns {Promise<TaskAttachment>}
   */
  async createAttachment(taskId, attachmentData) {
    return api.post(`/api/admin/tasks/${taskId}/attachments`, attachmentData);
  },

  /**
   * @param {string} taskId
   * @returns {Promise<TaskAttachment[]>}
   */
  async getAttachments(taskId) {
    const data = await api.get(`/api/admin/tasks/${taskId}/attachments`);
    assertArray(data, 'taskService.getAttachments');
    return data;
  },

  /**
   * @param {string} attachmentId
   * @returns {Promise<void>}
   */
  async deleteAttachment(attachmentId) {
    return api.del(`/api/admin/tasks/attachments/${attachmentId}`);
  },

  // ================================
  // ANALYTICS
  // ================================

  /**
   * @param {string} [assignedTo]
   * @returns {Promise<TaskAnalytics>}
   */
  async getAnalytics(assignedTo = null) {
    const params = assignedTo ? `?assigned_to=${assignedTo}` : '';
    const data = await api.get(`/api/admin/tasks/analytics/overview${params}`);
    assertShape(data, ['total_tasks'], 'taskService.getAnalytics');
    return data;
  },

  /**
   * @returns {Promise<TeamWorkload[]>}
   */
  async getTeamWorkload() {
    const data = await api.get('/api/admin/tasks/analytics/workload');
    // Backend returns a bare array of per-user workload entries, not an
    // object with a 'members' key — assertShape here threw on every valid call.
    assertArray(data, 'taskService.getTeamWorkload');
    return data;
  },
};

export default taskService;
